import { ReactNode, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { useAppStore } from '../../stores/useAppStore';
import { cn } from '../../lib/utils';
import { useOnboardingStatus } from '@/hooks/useOnboarding';
import { WelcomeModal } from '@/components/onboarding/WelcomeModal';
import { GuidedTour } from '@/components/onboarding/GuidedTour';
import { TrialBanner } from '@/components/billing/TrialBanner';

interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const { sidebarOpen } = useAppStore();
  const { data: onboardingStatus } = useOnboardingStatus();
  const navigate = useNavigate();

  const [showWelcome, setShowWelcome] = useState(false);
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    if (onboardingStatus && !onboardingStatus.hasCompletedOnboarding) {
      setShowWelcome(true);
    }
  }, [onboardingStatus]);

  const handleWelcomeComplete = (result: { projectId?: string }) => {
    setShowWelcome(false);
    const tourCompleted = localStorage.getItem('projectflow_tour_completed');
    if (!tourCompleted && result.projectId) {
      navigate(`/projects/${result.projectId}`);
      // Delay tour slightly to let the page load
      setTimeout(() => setShowTour(true), 1000);
    } else if (result.projectId) {
      navigate(`/projects/${result.projectId}`);
    }
  };

  const handleTourComplete = () => {
    setShowTour(false);
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <Sidebar />

      {/* Main content area */}
      <div
        className={cn(
          'flex flex-1 flex-col transition-all duration-300',
          sidebarOpen ? 'ml-64' : 'ml-16'
        )}
      >
        {/* Trial banner */}
        <TrialBanner />

        {/* Header */}
        <Header />

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>

      {/* Onboarding */}
      <WelcomeModal isOpen={showWelcome} onComplete={handleWelcomeComplete} />
      <GuidedTour isActive={showTour} onComplete={handleTourComplete} />
    </div>
  );
}
